// ATE-CR-117
import { useEffect, useId, useRef, useState } from "react";
import type { SyntheticEvent } from "react";

import type { EscalationListItem, EscalationResolution } from "@/api/escalations.api";

export interface EscalationActionModalProps {
  readonly open: boolean;
  readonly action: EscalationResolution;
  readonly escalation: EscalationListItem;
  onConfirm(note: string): void;
  onCancel(): void;
}

// ATE-CR-117
export function EscalationActionModal({
  open,
  action,
  escalation,
  onConfirm,
  onCancel,
}: EscalationActionModalProps): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const messageId = useId();
  const noteId = useId();
  const [note, setNote] = useState("");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      setNote("");
      el.showModal();
    }
    if (!open && el.open) el.close();
  }, [open]);

  // Native <dialog> fires `cancel` on ESC. Forward to onCancel and prevent
  // default so parent state drives the close (keeps state in sync).
  const handleNativeCancel = (event: SyntheticEvent<HTMLDialogElement>): void => {
    event.preventDefault();
    onCancel();
  };

  const title =
    action === "commit" ? "Commit escalation" : "Release escalation";

  const message =
    action === "commit"
      ? "This will resolve the hold and write a DEDUCTION (commit) row. Continue?"
      : "This will resolve the hold and write a no transaction (release) row. Continue?";

  return (
    <dialog
      ref={ref}
      role="dialog"
      aria-labelledby={titleId}
      aria-describedby={messageId}
      onCancel={handleNativeCancel}
      className="escalation-action-modal"
    >
      <div className="escalation-action-modal__content">
        <h2 id={titleId} className="escalation-action-modal__title">
          {title}
        </h2>
        <p id={messageId} className="escalation-action-modal__message">
          {message}
        </p>
        <p className="escalation-action-modal__meta">
          Request:{" "}
          <code>{escalation.request_id.slice(0, 8)}</code> — tenant{" "}
          <code>{escalation.tenant_id.slice(0, 8)}</code>
        </p>
        <div className="escalation-action-modal__note-group">
          <label htmlFor={noteId} className="escalation-action-modal__note-label">
            Note (optional)
          </label>
          <textarea
            id={noteId}
            className="escalation-action-modal__note-textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
          />
        </div>
        <div className="escalation-action-modal__actions">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => onConfirm(note)}
          >
            Confirm
          </button>
        </div>
      </div>
    </dialog>
  );
}
